import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useMemo } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import { customToast } from "../../../components/notify";
import { useUpdateProjectMutation } from "../services";
import { IProjectInput, IProjectTypes, IUpdateProjectReq } from "../types";
import { ProjectInfoSchema } from "../validations";

interface IProps {
    projectInfo: Partial<IProjectTypes>;
}
const useEditProjectInfo = ({ projectInfo }: IProps) => {
    const [updateProject, { isLoading, error, isSuccess }] = useUpdateProjectMutation();

    const defaultValues = useMemo(() => {
        const { classify_category, ...rest } = projectInfo;

        let classify_category_tags: { tag: string }[] = [];
        if (classify_category && classify_category.trim() !== "") {
            // 透過 / 分割，並去除前綴數字，例如: "1.景點和地標" -> "景點和地標"
            classify_category_tags = classify_category
                .split("/")
                .map((item) => {
                    // Strip numbering: "1.景點和地標" -> "景點和地標"
                    const cleaned = item.replace(/^\d+\./, "").trim();
                    return { tag: cleaned };
                })
                .filter((item) => item.tag !== "");
        }

        return {
            ...rest,
            classify_category_tags,
        } as IProjectInput;
    }, [projectInfo]);

    const methods = useForm<IProjectInput>({
        defaultValues,
        resolver: yupResolver(ProjectInfoSchema()),
        mode: "all",
    });

    const handleFormSubmit: SubmitHandler<IProjectInput> = async (data) => {
        const { classify_category_tags, ...restData } = data;

        let classify_category = "";
        if (classify_category_tags && classify_category_tags.length > 0) {
            const validTags = classify_category_tags.filter((item) => item.tag && item.tag.trim() !== "");
            if (validTags.length > 0) {
                // 添加前綴數字，例如: "景點和地標" -> "1.景點和地標"
                classify_category = validTags.map((item, index) => `${index + 1}.${item.tag.trim()}`).join("/");
            }
        }

        const updateData: IUpdateProjectReq = {
            ...restData,
            classify_category,
        };

        await updateProject(updateData);
    };

    const handleCancel = () => {
        methods.reset();
    };
    const handleEdit = () => {
        methods.handleSubmit(handleFormSubmit)();
    };

    useEffect(() => {
        if (isSuccess) {
            customToast.success("Successfully.");
            methods.reset(methods.getValues());
        }
        if (error) {
            customToast.error("Update project information failed.");
        }
    }, [isSuccess, methods, error]);
    return {
        methods,
        handleEdit,
        handleCancel,
        isLoading,
    };
};
export default useEditProjectInfo;
